import React, { useState, useEffect, useMemo } from 'react';
import { Grid, TextField, InputAdornment } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import useTriggerAZapStore, { getTriggerAZapStore } from './useTriggerAZapStore';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import GenerateToken from '../Component/GenerateToken';
import ErrorIcon from '@mui/icons-material/Error';
import '../WebhookImport/WebhookImport.css';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import DataPayloadToZap from './DataPayloadToZap';

const TriggerAZapTemplate = (props) => {
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updateState, setUpdateState] = useState(false);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getTriggerAZapStore(props.component_id), [props.component_id]);

  const placeholders = store((s) => s.placeholders);
  const storeData = store((s) => s.data);

  const forceUpdateState = () => {
    setUpdateState((prev) => !prev);
  };

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);
    const waitPromise = new Promise((resolve) => {
      const wait = setTimeout(() => {
        clearTimeout(wait);
        resolve('wait done');
      }, 2000);
    });

    waitPromise
      .then(() => {
        return send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );
      })
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setComponentData(components[0]);
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);
            setStatus(components[0].status);
            if (components[0].componentData && components[0].componentData) {
              if (components[0].componentData.token) {
                setHasToken(true);
              }
              setLoading(false);
            }
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
        props.setChildTemplateLoaded(true);
      })
      .catch((err) => {
        setLoading(false);
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const componentName = useProjectStore.getState().componentName;
    if (componentName !== undefined) {
      store.getState().setName(componentName);
    }
    const unsub = useProjectStore.subscribe(
      (s) => s.componentName,
      (name) => {
        store.getState().setName(name);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (placeholder && placeholder.selected) {
          const { style, key } = placeholder.selected;
          insertPlaceholder(style.default, key, style);
        }
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style) => {
    const currentPlaceholders = [...store.getState().placeholders];
    const position = store.getState().position;

    if (!currentPlaceholders[position]) {
      currentPlaceholders[position] = [];
    }
    currentPlaceholders[position].push({ label, text, style });
    store.getState().setPlaceholders(currentPlaceholders);
  };

  const changeTokenStatus = (tokenStatus) => {
    setHasToken(tokenStatus);
    props.changeTokenStatus(tokenStatus);
  };

  if (!loading) {
    const componentName = useProjectStore.getState().componentName;

    return (
      <CustomPaper className="componentPaper" style={{ padding: '24px' }}>
        <Grid container className="componentGrid" spacing={2}>
          <Grid item xs={12}>
            <b>Component ID</b>
          </Grid>

          <Grid item xs={12}>
            <div>Copy and paste this Component ID into Zapier</div>
            <TextField
              disabled={true}
              className={`key_padding ${props.isExpanded && 'textFieldWebhook'}`}
              variant="outlined"
              fullWidth
              value={props.component_id}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyToClipboard text={props.component_id}>
                      <FileCopyIcon className="custom-icon-btn" />
                    </CopyToClipboard>
                  </InputAdornment>
                ),
              }}
              style={{ marginTop: '8px' }}
            ></TextField>
          </Grid>

          <Grid item xs={12}>
            <b>Security Token</b>
          </Grid>

          <Grid item xs={12}>
            <span> Generate a token and insert the value into Zapier </span>

            {!hasToken ? (
              <span
                className="err-text"
                style={{ justifyContent: 'flex-start', fontWeight: 'bold', margin: '16px 0' }}
              >
                <ErrorIcon className="iconDiscard" />A token must be generated before the Trigger
                a Zap component can be used.
              </span>
            ) : (
              <span
                className="err-text"
                style={{ justifyContent: 'flex-start', fontWeight: 'bold', margin: '16px 0' }}
              >
                <ErrorIcon className="iconDiscard" />A token has already been generated. If you
                have lost this token, you will need to generate a new one.
              </span>
            )}

            {!loading && (
              <GenerateToken
                hasToken={(tokenStatus) => {
                  setHasToken(tokenStatus);
                }}
                tokenStatus={hasToken}
                name={componentName}
                component_id={props.component_id}
                data={storeData}
                generateTokenUrl={'component-service/trigger_a_zap/generate-token'}
                deleteTokenUrl={'component-service/trigger_a_zap/delete-token'}
                componentType="trigger_a_zap"
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <DataPayloadToZap
              availablePlaceholders={props.availablePlaceholders}
              placeholders={placeholders}
            />
          </Grid>
        </Grid>
      </CustomPaper>
    );
  } else return <IndividualComponentLoader />;
};

export default TriggerAZapTemplate;
